import { useState } from 'react';
import { FIELDS, GENERAL_QUESTIONS, FIELD_QUESTIONS } from './questions';
import './App.css';


const SCREEN = { LANDING: 'landing', INFO: 'info', TEST: 'test', SUBMITTING: 'submitting', DONE: 'done' };

const APPS_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzB2vQK5YdC2r6kABRcpBl-U0pW6LozsZCXaLcrp5NaigE6qFkK4-ZTYtSY9kxs-Lc/exec';


function cn(...classes) { return classes.filter(Boolean).join(' '); }


function Tag({ children, color = 'blue' }) {
  return <span className={`tag tag-${color}`}>{children}</span>;
}

function Badge({ type }) {
  const map = { mcq: ['MCQ', 'blue'], short: ['Short Answer', 'amber'], practical: ['Practical', 'green'] };
  const [label, color] = map[type] || ['?', 'blue'];
  return <Tag color={color}>{label}</Tag>;
}

function ProgressBar({ current, total }) {
  const pct = Math.round((current / total) * 100);
  return (
    <div className="progress-wrap">
      <div className="progress-track">
        <div className="progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="progress-label">{current} / {total}</span>
    </div>
  );
}


function MCQQuestion({ q, value, onChange }) {
  return (
    <div className="options-grid">
      {q.options.map((opt, i) => (
        <button
          key={i}
          className={cn('option-btn', value === i && 'selected')}
          onClick={() => onChange(i)}
          type="button"
        >
          <span className="option-letter">{String.fromCharCode(65 + i)}</span>
          <span className="option-text">{opt}</span>
          {value === i && <span className="option-check">✓</span>}
        </button>
      ))}
    </div>
  );
}

function OpenQuestion({ q, value, onChange }) {
  return (
    <div className="open-wrap">
      <div className="manual-notice">
        <span className="manual-icon">👁</span>
        <span>This answer will be reviewed manually by the team</span>
      </div>
      <textarea
        rows={q.type === 'practical' ? 10 : 6}
        placeholder={q.placeholder}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className={q.type === 'practical' ? 'mono' : ''}
        spellCheck={q.type !== 'practical'}
      />
    </div>
  );
}

function QuestionCard({ q, index, total, value, onChange, sectionLabel }) {
  return (
    <div className="q-card fade-up">
      <div className="q-meta">
        <span className="q-section-label">{sectionLabel}</span>
        <div className="q-badges">
          <Badge type={q.type} />
          <Tag color="dim">{q.points} pts</Tag>
        </div>
      </div>
      <div className="q-number">Q{index + 1} <span className="q-of">of {total}</span></div>
      <p className="q-text">{q.question}</p>
      {q.type === 'mcq'
        ? <MCQQuestion q={q} value={value} onChange={onChange} />
        : <OpenQuestion q={q} value={value} onChange={onChange} />
      }
    </div>
  );
}


function LandingScreen({ onStart }) {
  return (
    <div className="screen landing-screen fade-in">
      <div className="landing-inner">
        <div className="landing-badge">GDG on Campus</div>
        <h1 className="landing-title">Development<br />Department<br />Test</h1>
        <p className="landing-sub">2025 – 2026 Recruitment</p>
        <div className="landing-info-grid">
          <div className="info-card">
            <span className="info-icon">🧠</span>
            <span className="info-label">General Section</span>
            <span className="info-val">5 questions · 20 pts</span>
          </div>
          <div className="info-card">
            <span className="info-icon">🎯</span>
            <span className="info-label">Field Section</span>
            <span className="info-val">12 questions · 80 pts</span>
          </div>
          <div className="info-card">
            <span className="info-icon">✅</span>
            <span className="info-label">Passing Grade</span>
            <span className="info-val">35 / 100</span>
          </div>
          <div className="info-card">
            <span className="info-icon">⏱</span>
            <span className="info-label">Estimated Time</span>
            <span className="info-val">45 – 60 min</span>
          </div>
        </div>
        <div className="landing-rules">
          <p>Answer the General Section, then <strong>only</strong> your chosen field section.</p>
          <p>MCQs are auto-graded · Short answers and code are reviewed by the team.</p>
          <p>Be honest — the interview is based on your answers here.</p>
        </div>
        <button className="btn-primary" onClick={onStart}>Start Application →</button>
      </div>
    </div>
  );
}

function InfoScreen({ onSubmit }) {
  const [form, setForm] = useState({ name: '', email: '', university: '', major: '', year: '', github: '', field: '' });
  const [error, setError] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    const required = ['name', 'email', 'university', 'field'];
    for (const k of required) {
      if (!form[k].trim()) { setError(`Please fill in your ${k}.`); return; }
    }
    if (!form.email.includes('@')) { setError('Please enter a valid email.'); return; }
    setError('');
    onSubmit(form);
  };

  return (
    <div className="screen info-screen fade-in">
      <div className="info-inner">
        <div className="section-title-row">
          <span className="section-eyebrow">Step 1 of 2</span>
          <h2 className="section-heading">Your Information</h2>
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label>Full Name *</label>
            <input placeholder="Your full name" value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Email *</label>
            <input type="email" placeholder="your@email.com" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div className="form-group">
            <label>University *</label>
            <input placeholder="e.g. University of Jordan" value={form.university} onChange={e => set('university', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Major</label>
            <input placeholder="e.g. Computer Science" value={form.major} onChange={e => set('major', e.target.value)} />
          </div>
          <div className="form-group">
            <label>Year of Study</label>
            <input placeholder="e.g. 2nd year" value={form.year} onChange={e => set('year', e.target.value)} />
          </div>
          <div className="form-group">
            <label>GitHub Profile</label>
            <input placeholder="github.com/username" value={form.github} onChange={e => set('github', e.target.value)} />
          </div>
        </div>

        <div className="form-group field-group">
          <label>Choose Your Field * <span className="label-hint">(answer only this section's questions)</span></label>
          <div className="field-grid">
            {FIELDS.map(f => (
              <button
                key={f.id}
                type="button"
                className={cn('field-btn', form.field === f.id && 'selected')}
                onClick={() => set('field', f.id)}
              >
                <span className="field-emoji">{f.emoji}</span>
                <span className="field-label">{f.label}</span>
                {form.field === f.id && <span className="field-check">✓</span>}
              </button>
            ))}
          </div>
        </div>

        {error && <div className="form-error">⚠ {error}</div>}

        <button className="btn-primary" onClick={handleSubmit}>Begin Test →</button>
      </div>
    </div>
  );
}

function TestScreen({ applicant, onSubmit }) {
  const fieldInfo = FIELDS.find(f => f.id === applicant.field);
  const fieldQs = FIELD_QUESTIONS[applicant.field] || [];
  const allQuestions = [
    ...GENERAL_QUESTIONS.map(q => ({ ...q, _section: 'General' })),
    ...fieldQs.map(q => ({ ...q, _section: fieldInfo?.label })),
  ];
  const total = allQuestions.length;

  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);

  const q = allQuestions[current];
  const isLast = current === total - 1;
  const isGeneral = current < GENERAL_QUESTIONS.length;

  const setAnswer = val => setAnswers(a => ({ ...a, [q.id]: val }));

  const next = () => {
    if (isLast) { setShowConfirm(true); } else { setCurrent(c => c + 1); window.scrollTo(0, 0); }
  };

  const prev = () => { if (current > 0) { setCurrent(c => c - 1); window.scrollTo(0, 0); } };

  const handleSubmit = () => {
    let mcqScore = 0;
    let mcqTotal = 0;
    const gradedAnswers = {};

    allQuestions.forEach(q => {
      if (q.type === 'mcq') {
        mcqTotal += q.points;
        const ans = answers[q.id];
        const correct = ans === q.correctAnswer;
        if (correct) mcqScore += q.points;
        gradedAnswers[q.id] = { answer: ans, correct, points: correct ? q.points : 0 };
      } else {
        gradedAnswers[q.id] = { answer: answers[q.id] || '', correct: null, points: null };
      }
    });

    onSubmit({ applicant, answers, gradedAnswers, mcqScore, mcqTotal });
  };

  const answered = Object.keys(answers).length;
  const unanswered = total - answered;

  return (
    <div className="screen test-screen">
      {/* Header */}
      <div className="test-header">
        <div className="test-header-inner">
          <div className="test-header-left">
            <span className="test-logo">GDGoC</span>
            <span className="test-sep">·</span>
            <span className="test-name">{applicant.name}</span>
          </div>
          <div className="test-header-right">
            <span className={cn('section-pill', isGeneral ? 'pill-blue' : 'pill-amber')}>
              {isGeneral ? '🧠 General' : `${fieldInfo?.emoji} ${fieldInfo?.label}`}
            </span>
          </div>
        </div>
        <ProgressBar current={current + 1} total={total} />
      </div>

      {/* Question */}
      <div className="test-body">
        <QuestionCard
          key={q.id}
          q={q}
          index={current}
          total={total}
          value={answers[q.id] ?? (q.type === 'mcq' ? undefined : '')}
          onChange={setAnswer}
          sectionLabel={isGeneral ? 'General Section' : `${fieldInfo?.label} Section`}
        />

        {/* Nav */}
        <div className="test-nav">
          <button className="btn-ghost" onClick={prev} disabled={current === 0}>← Previous</button>
          <span className="nav-status">{answered} answered · {unanswered} remaining</span>
          <button className="btn-primary" onClick={next}>
            {isLast ? 'Review & Submit' : 'Next →'}
          </button>
        </div>

        {/* Quick jump */}
        <div className="jump-grid">
          {allQuestions.map((qq, i) => (
            <button
              key={qq.id}
              className={cn('jump-btn', i === current && 'active', answers[qq.id] !== undefined && answers[qq.id] !== '' && 'answered')}
              onClick={() => { setCurrent(i); window.scrollTo(0, 0); }}
              title={`Q${i + 1}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </div>

      {/* Confirm dialog */}
      {showConfirm && (
        <div className="modal-overlay fade-in">
          <div className="modal fade-up">
            <h3 className="modal-title">Submit Test?</h3>
            <p className="modal-body">
              You've answered <strong>{answered}</strong> of <strong>{total}</strong> questions.
              {unanswered > 0 && <span className="modal-warn"> {unanswered} question{unanswered > 1 ? 's are' : ' is'} unanswered.</span>}
            </p>
            <p className="modal-body">Once submitted, you cannot go back.</p>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setShowConfirm(false)}>Go Back</button>
              <button className="btn-primary" onClick={handleSubmit}>Submit ✓</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SubmittingScreen() {
  return (
    <div className="screen center-screen fade-in">
      <div className="submitting-inner">
        <div className="spinner" />
        <p className="submitting-text">Submitting your answers…</p>
      </div>
    </div>
  );
}

function DoneScreen({ result }) {
  const { applicant, mcqScore, mcqTotal, gradedAnswers, allQuestions } = result;
  const fieldInfo = FIELDS.find(f => f.id === applicant.field);
  const openCount = Object.values(gradedAnswers).filter(a => a.correct === null).length;
  const maxMCQScore = mcqTotal;

  return (
    <div className="screen done-screen fade-in">
      <div className="done-inner">
        <div className="done-check">✓</div>
        <h2 className="done-title">Test Submitted!</h2>
        <p className="done-sub">Thank you, <strong>{applicant.name}</strong>. Your answers have been recorded.</p>

        <div className="done-card">
          <div className="done-row">
            <span>Field</span>
            <span>{fieldInfo?.emoji} {fieldInfo?.label}</span>
          </div>
          <div className="done-row">
            <span>MCQ Score (auto-graded)</span>
            <span className="done-score">{mcqScore} / {maxMCQScore}</span>
          </div>
          <div className="done-row">
            <span>Open Questions</span>
            <span className="done-pending">{openCount} pending manual review</span>
          </div>
          <div className="done-divider" />
          <div className="done-row">
            <span>Final Score</span>
            <span className="done-final">Will be sent after review</span>
          </div>
        </div>

        <div className="done-notice">
          <p>The GDGoC team will review your short answers and practical questions.</p>
          <p>Results will be communicated via email. Good luck! 🚀</p>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [screen, setScreen] = useState(SCREEN.LANDING);
  const [applicant, setApplicant] = useState(null);
  const [result, setResult] = useState(null);

  const handleInfoSubmit = (form) => {
    setApplicant(form);
    setScreen(SCREEN.TEST);
  };

  const handleTestSubmit = async (data) => {
    setScreen(SCREEN.SUBMITTING);

    const fieldInfo = FIELDS.find(f => f.id === data.applicant.field);
    const fieldQs = FIELD_QUESTIONS[data.applicant.field] || [];
    const allQs = [...GENERAL_QUESTIONS, ...fieldQs];

    const openAnswers = {};
    allQs.forEach(q => {
      if (q.type !== 'mcq') {
        openAnswers[q.id] = data.answers[q.id] || '';
      }
    });

    const payload = {
      ...data.applicant,
      fieldLabel: fieldInfo?.label,
      mcqScore: data.mcqScore,
      mcqTotal: data.mcqTotal,
      openAnswers: JSON.stringify(openAnswers),
      gradedAnswers: JSON.stringify(data.gradedAnswers),
      submittedAt: new Date().toISOString(),
    };

    try {
      await fetch(APPS_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (e) {
      console.error('Submission error:', e);
    }

    const allQuestions = [...GENERAL_QUESTIONS, ...fieldQs];
    setResult({ ...data, allQuestions });
    setScreen(SCREEN.DONE);
  };

  return (
    <div className="app">
      {screen === SCREEN.LANDING    && <LandingScreen onStart={() => setScreen(SCREEN.INFO)} />}
      {screen === SCREEN.INFO       && <InfoScreen onSubmit={handleInfoSubmit} />}
      {screen === SCREEN.TEST       && <TestScreen applicant={applicant} onSubmit={handleTestSubmit} />}
      {screen === SCREEN.SUBMITTING && <SubmittingScreen />}
      {screen === SCREEN.DONE       && <DoneScreen result={result} />}
    </div>
  );
}
